import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { OrderItemTaskModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/orderItemTask/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/orderItemTask/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/orderItemTask/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/orderItemTask/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/orderItemTask/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/orderItemTask/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/orderItemTask/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/orderItemTask/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/orderItemTask/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/orderItemTask/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/orderItemTask/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/orderItemTask/DELETE_FAILURE'

export type OrderItemTaskModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: OrderItemTaskModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function hydrateOrderItemTask(orderItemTask: OrderItemTaskModel) {
  return (dispatch: AppDispatch) => {
    if (orderItemTask?.id){
      const normalizedJson = normalize(orderItemTask, Schemas.ORDER_ITEM_TASK)
      // @ts-expect-error TS2345
      dispatch(updateEntities(normalizedJson))
    } else {
      console.error('Could not hydrate undefined order item task.')
    }
  }
}

export function loadOrderItemTasks(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().orderItemTasks.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/order_item_tasks.json', options)
      .then(({ data }: { data: OrderItemTaskModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadOrderItemTask(orderItemTaskId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.orderItemTasks.loadedForKeys]
    const loadedIds = [...state.orderItemTasks.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (orderItemTaskId && !loadedIds.includes(orderItemTaskId)){
      loadedIds.push(orderItemTaskId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/order_item_tasks/${orderItemTaskId}.json`, options)
      .then(({ data }: { data: OrderItemTaskModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createOrderItemTask(orderItemTask: OrderItemTaskModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order_item_task: {
        ...orderItemTask,
        // @ts-expect-error TS2551
        instruction_image: orderItemTask.instruction_image_file,
      },
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/order_item_tasks.json', options, config)
      .then(({ data }: { data: OrderItemTaskModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateOrderItemTask(orderItemTask: Partial<OrderItemTaskModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_item_task: {
        ...orderItemTask,
        // @ts-expect-error TS2551
        instruction_image: orderItemTask.instruction_image_file,
      },
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/order_item_tasks/${orderItemTask.id}.json`, options, config)
      .then(({ data }: { data: OrderItemTaskModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function completeOrderItemTask(orderItemTask: Partial<OrderItemTaskModel>) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_item_task_id: orderItemTask.id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/order_item_tasks/${orderItemTask.id}/complete.json`, {}, config)
      .then(({ data }: { data: OrderItemTaskModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateSortOrder(orderItemTaskIds: number[]) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_item_task_ids: orderItemTaskIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/order_item_tasks/update_sort_order.json', {}, config)
      .then(({ data }: { data: OrderItemTaskModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteOrderItemTask(orderItemTask: OrderItemTaskModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/order_item_tasks/${orderItemTask.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_TASK)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: OrderItemTaskModuleState = initialState,
  action: Action = { type: '' },
): OrderItemTaskModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
